import { Arrange, TodoList2, Trash } from '@griply/assets';
import {
  ALL_TASKS_LINKED_ITEM_ID,
  CalendarDetailsVisibility,
  ID,
  LinkedItemType,
  SyncMode,
} from '@griply/types';
import { getLinkedItemColor } from '@griply/utils';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconFormIcons, Target } from 'shared/assets/icons';
import { GoalTreeContextMenu } from 'shared/components/connected/goal-context-menu';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { FormSelectTrigger } from 'shared/components/ui/form-select-trigger';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { ConnectionCalendar } from 'shared/hooks/queries/use-external-connections-calendars-queries';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';

import { CalendarSelect, CalendarSelectValue } from './calendar-select';
import * as Styled from './calendar-sync-options.style';

export type CalendarSyncRowValue = {
  linkedItemId: ID | null;
  calendar: CalendarSelectValue | null;
  mode: SyncMode | null;
  detailsVisibility: CalendarDetailsVisibility | null;
};

export type CalendarSyncOptionRowProps = CalendarSyncRowValue & {
  goals: Goal[];
  lifeAreas: LifeArea[];
  calendars: ConnectionCalendar[];
  selectableCalendars: ConnectionCalendar[];
  onChangeLinkedItem: (value: { id: ID; type: LinkedItemType } | null) => void;
  onChangeCalendar: (value: CalendarSelectValue) => void;
  onChangeMode: (mode: SyncMode) => void;
  onChangeVisibility: (visibility: CalendarDetailsVisibility) => void;
  onRemove: () => void;
};

export const CalendarSyncOptionRow: React.FC<CalendarSyncOptionRowProps> = ({
  linkedItemId,
  calendar,
  mode,
  detailsVisibility,
  goals,
  lifeAreas,
  calendars,
  selectableCalendars,
  onChangeLinkedItem,
  onChangeCalendar,
  onChangeMode,
  onChangeVisibility,
  onRemove,
}) => {
  const { t } = useTranslation();
  const linkedItemRef = useRef<HTMLDivElement>(null);
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const isAllTasks = linkedItemId === ALL_TASKS_LINKED_ITEM_ID;
  const allTasksLabel = t(
    'forms.user-settings.sections.calendar.sync-tasks.all-tasks',
  );

  // Resolved against the *unfiltered* goals/life areas so a sync targeting an archived or
  // completed item still renders its name — the picker's own lists exclude those.
  const { linkedItem, isGoal } = useMemo(() => {
    const goal = goals.find(({ id }) => id === linkedItemId);
    if (goal) {
      return { linkedItem: goal, isGoal: true };
    }
    return {
      linkedItem: lifeAreas.find(({ id }) => id === linkedItemId),
      isGoal: false,
    };
  }, [goals, lifeAreas, linkedItemId]);

  const linkedItemColor = useMemo(() => {
    if (!linkedItem) {
      return undefined;
    }
    return isGoal
      ? getLinkedItemColor(linkedItem.id, { goals, lifeAreas }) ?? undefined
      : linkedItem.color ?? undefined;
  }, [linkedItem, isGoal, goals, lifeAreas]);

  const currentSelection = useMemo(() => {
    if (!linkedItem) {
      return undefined;
    }
    return isGoal ? { goalId: linkedItem.id } : { lifeAreaId: linkedItem.id };
  }, [linkedItem, isGoal]);

  // The tree reports a target container: a clicked goal arrives with its id as the last
  // `parentIds` entry; a clicked life area arrives via `lifeAreaId`.
  const onSelectLinkedItem = (parentIds: ID[], lifeAreaId: ID | null) => {
    if (parentIds.length) {
      onChangeLinkedItem({
        id: parentIds[parentIds.length - 1],
        type: LinkedItemType.Goal,
      });
      closeMenu();
      return;
    }
    if (lifeAreaId) {
      onChangeLinkedItem({ id: lifeAreaId, type: LinkedItemType.LifeArea });
    }
    closeMenu();
  };

  const onSelectAllTasks = () => {
    onChangeLinkedItem({
      id: ALL_TASKS_LINKED_ITEM_ID,
      type: LinkedItemType.AllTasks,
    });
    closeMenu();
  };

  const onSelectMode = (value?: string) => {
    if (!value) {
      return;
    }

    onChangeMode(value as SyncMode);
  };

  const onSelectVisibility = (value?: string) => {
    if (!value) {
      return;
    }

    onChangeVisibility(value as CalendarDetailsVisibility);
  };

  const modeOptions = [
    {
      value: SyncMode.Automatic,
      label: t(
        'forms.user-settings.sections.calendar.sync-tasks.mode.automatic',
      ),
    },
    {
      value: SyncMode.Manual,
      label: t('forms.user-settings.sections.calendar.sync-tasks.mode.manual'),
    },
  ];

  const visibilityOptions = [
    {
      value: CalendarDetailsVisibility.Visible,
      label: t(
        'forms.user-settings.sections.calendar.sync-tasks.visibility.visible',
      ),
    },
    {
      value: CalendarDetailsVisibility.Hidden,
      label: t(
        'forms.user-settings.sections.calendar.sync-tasks.visibility.hidden',
      ),
    },
  ];

  const placeholder = t(
    'forms.user-settings.sections.calendar.sync-tasks.placeholder',
  );

  const linkedItemIcon = isAllTasks
    ? TodoList2
    : linkedItem?.iconName
      ? IconFormIcons[linkedItem.iconName]
      : Target;

  return (
    <Styled.Row>
      <div ref={linkedItemRef}>
        <FormSelectTrigger
          onClick={openMenu}
          isPlaceholder={!isAllTasks && !linkedItem}
          size={SelectSize.ExtraSmall}
          noOverflow
          ariaHasPopup="dialog"
          ariaExpanded={menuOpen}
        >
          <Styled.SelectValue>
            <Styled.SelectIcon $color={linkedItemColor}>
              <Icon icon={linkedItemIcon} />
            </Styled.SelectIcon>
            <Styled.SelectLabel>
              {isAllTasks ? allTasksLabel : linkedItem?.name ?? placeholder}
            </Styled.SelectLabel>
          </Styled.SelectValue>
        </FormSelectTrigger>

        {menuOpen && (
          <GoalTreeContextMenu
            hideNoLifeArea
            onSelect={onSelectLinkedItem}
            currentSelection={currentSelection}
            allTasksLabel={allTasksLabel}
            isAllTasksSelected={isAllTasks}
            onSelectAllTasks={onSelectAllTasks}
            referenceElement={linkedItemRef}
            position="bottom"
            onClose={closeMenu}
          />
        )}
      </div>

      <Styled.SwapIcon>
        <Icon icon={Arrange} size={16} strokeWidth={1.5} />
      </Styled.SwapIcon>

      <CalendarSelect
        calendars={calendars}
        selectableCalendars={selectableCalendars}
        value={calendar}
        onChange={onChangeCalendar}
      />

      <CustomFormSelect
        placeholder={placeholder}
        options={modeOptions}
        value={mode ?? undefined}
        onChange={onSelectMode}
        size={SelectSize.ExtraSmall}
        noOverflow
      />

      <CustomFormSelect
        placeholder={placeholder}
        options={visibilityOptions}
        value={detailsVisibility ?? undefined}
        onChange={onSelectVisibility}
        size={SelectSize.ExtraSmall}
        noOverflow
      />

      <IconButton
        type="button"
        size={IconButtonSizes.Small}
        color={IconButtonColors.Default}
        icon={Trash}
        onClick={onRemove}
        aria-label={t(
          'forms.user-settings.sections.calendar.sync-tasks.remove',
        )}
      />
    </Styled.Row>
  );
};
